import { HermesBadge } from "@/backoffice/hermes-ui";
import { cn } from "@/utils/cn";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router";
import type { NavIcon } from "./backofficeNavItems";
import { ICON_HOVER, ICON_POP } from "./backofficeIconMotion";
import { BackofficeNavText } from "./BackofficeNavText";

type BackofficeNavLeafProps = {
  label: string;
  path: string;
  icon: NavIcon;
  isActive: boolean;
  isCollapsed: boolean;
  /** Funcionalidade ainda não liberada em produção: aparece travada. */
  isLocked?: boolean;
  /** Item de dentro de um grupo: menor e sem o peso do item de topo. */
  isChild?: boolean;
  /** Bolinha de aviso (mensagens não lidas, template com problema). */
  unreadTitle?: string;
  /** Página fora do backoffice: abre em nova aba, com ícone de saída. */
  opensInNewTab?: boolean;
  onNavigate: () => void;
};

/**
 * Um item de navegação do backoffice: ícone + texto. Ativo = fundo de marca
 * suave, texto de marca e ícone preenchido; no hover o ícone sobe e cresce
 * (davincii). Travado ("Em breve") não navega. O padding é o mesmo com o menu
 * aberto ou recolhido: só o texto entra e sai, animado.
 */
export const BackofficeNavLeaf = ({
  label,
  path,
  icon: Icon,
  isActive,
  isCollapsed,
  isLocked = false,
  isChild = false,
  unreadTitle,
  opensInNewTab = false,
  onNavigate,
}: BackofficeNavLeafProps) => {
  const base = cn(
    "group relative flex items-center rounded-xl px-3.5 text-sm font-medium",
    isChild ? "min-h-9 py-1.5" : "h-10"
  );
  const icon = (
    <span className="relative shrink-0">
      <Icon
        // key: o pop recomeça cada vez que o item vira o ativo.
        key={isActive ? "on" : "off"}
        size={isChild ? 16 : 18}
        className={cn(
          "transition-[fill] duration-300",
          !isLocked && ICON_HOVER,
          isActive && cn("fill-current/20", ICON_POP)
        )}
      />
      {unreadTitle ? (
        <span
          className={cn(
            "bg-hermes-primary ring-hermes-bg absolute -top-1 -right-1 size-2 rounded-full ring-2",
            "transition-opacity duration-300 motion-reduce:transition-none",
            isCollapsed ? "opacity-100" : "opacity-0"
          )}
        />
      ) : null}
    </span>
  );

  if (isLocked) {
    return (
      <div
        aria-disabled="true"
        aria-label={isCollapsed ? `${label} (em breve)` : undefined}
        title={isCollapsed ? `${label} (em breve)` : "Em breve"}
        className={cn(base, "text-hermes-muted-fg/50 cursor-not-allowed")}
      >
        {icon}
        <BackofficeNavText visible={!isCollapsed} wrap={isChild}>
          {label}
        </BackofficeNavText>
        <BackofficeNavText trailing visible={!isCollapsed}>
          <HermesBadge className="text-[0.625rem] tracking-wide uppercase">
            Em breve
          </HermesBadge>
        </BackofficeNavText>
      </div>
    );
  }

  return (
    <Link
      to={path}
      onClick={onNavigate}
      {...(opensInNewTab
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      aria-current={isActive ? "page" : undefined}
      aria-label={
        isCollapsed
          ? opensInNewTab
            ? `${label} (abre em nova aba)`
            : label
          : undefined
      }
      title={isCollapsed ? label : undefined}
      className={cn(
        base,
        "hermes-focus-ring cursor-pointer transition-colors duration-150 motion-reduce:transition-none",
        isActive
          ? "bg-hermes-accent-soft text-hermes-accent font-semibold"
          : "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg"
      )}
    >
      {icon}
      <BackofficeNavText visible={!isCollapsed} wrap={isChild}>
        {label}
        {opensInNewTab ? (
          <span className="sr-only"> (abre em nova aba)</span>
        ) : null}
      </BackofficeNavText>
      {opensInNewTab ? (
        <BackofficeNavText trailing visible={!isCollapsed}>
          <ExternalLink aria-hidden="true" size={14} />
        </BackofficeNavText>
      ) : null}
      {unreadTitle ? (
        <BackofficeNavText trailing visible={!isCollapsed}>
          <span
            className="bg-hermes-primary block size-2 rounded-full"
            title={unreadTitle}
          >
            <span className="sr-only">{unreadTitle}</span>
          </span>
        </BackofficeNavText>
      ) : null}
    </Link>
  );
};
